/** Ops Logs — Daily / Weekly / Monthly templates from the user's ACE-Ops-Templates HTML. */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Save, Printer, Loader2, Zap, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const num = (v) => parseFloat(String(v || '').replace(/[^0-9.-]/g, '')) || 0;
const $ = (n) => '$' + n.toLocaleString('en-US', { maximumFractionDigits: 2 });

const DAILY_VEHICLES = ['Mercedes-Benz S-Class', 'Cadillac CT6 / XTS', 'Lincoln Continental', 'Cadillac Escalade', 'Lincoln Navigator', 'Mercedes-Benz GLS', 'Sprinter Standard', 'Sprinter Lux Build', 'Ford Transit Executive', 'Stretch Limousine', 'Exotic / Supercar', 'Mini Coach', 'Full-Size Motorcoach', 'Client Vehicle (Driver Only)'];
const SERVICES = ['Airport Transfer', 'Prom / Event Luxury', 'Corporate / Executive', 'Concert / Nightlife', 'Long Distance', 'Driver Only', 'Photo / Media', 'Consulting'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const EXPENSES = ['Fuel', 'Tolls', 'Cleaning', 'Vehicle Rental', 'Misc / Other'];
const CHANNELS = ['ACE Direct — YourAutoConcierge.com', 'Referral / Word of Mouth', 'Driven Collective / Affiliate', 'Membership — Lobby / Condo / Penthouse', 'Podcast / Media — DrivenOrDriving.com'];

// Generic schemas: grid sections = rows × cols; list sections = simple lines
const SCHEMAS = {
  daily: [
    { id: 'drivers', title: 'Driver Deployment', type: 'grid', rows: ['JD Lawrence', 'Corey', 'Affiliate Driver 1', 'Affiliate Driver 2'], cols: [{ k: 'rate', l: 'Rate/Hr' }, { k: 'owed', l: 'Money Owed' }, { k: 'notes', l: 'Notes', wide: true }] },
    { id: 'vehicles', title: 'Vehicle Utilization', type: 'grid', rows: DAILY_VEHICLES, cols: [{ k: 'runs', l: 'Runs' }, { k: 'hours', l: 'Hours' }, { k: 'gross', l: 'Gross' }, { k: 'notes', l: 'Notes', wide: true }] },
    { id: 'expenses', title: 'Expenses', type: 'grid', rows: EXPENSES, cols: [{ k: 'amt', l: 'Amount' }, { k: 'note', l: 'Note', wide: true }] },
    { id: 'extra', title: 'Mileage & Collections', type: 'grid', rows: ['Today'], cols: [{ k: 'miles', l: 'Total Miles' }, { k: 'collected', l: 'Collected Today' }, { k: 'ar', l: 'Total AR Outstanding' }] },
    { id: 'invoices', title: 'Open Invoices', type: 'list', lines: 4 },
    { id: 'open', title: 'Open Items', type: 'list', lines: 3 },
    { id: 'confirmed', title: 'Confirmed & Collected', type: 'list', lines: 3 },
  ],
  weekly: [
    { id: 'summary', title: 'Weekly Summary', type: 'grid', rows: ['This Week'], cols: [{ k: 'days', l: 'Days Active' }, { k: 'bookings', l: 'Total Bookings' }, { k: 'gross', l: 'Gross Revenue' }, { k: 'cost', l: 'Driver / Cost' }, { k: 'net', l: 'Net to ACE' }] },
    { id: 'days', title: 'Day by Day', type: 'grid', rows: DAYS, cols: [{ k: 'jobs', l: 'Jobs' }, { k: 'gross', l: 'Gross' }, { k: 'cost', l: 'Driver Cost' }, { k: 'net', l: 'Net' }, { k: 'out', l: 'Outstanding' }] },
    { id: 'mix', title: 'Service Mix This Week', type: 'grid', rows: SERVICES, cols: [{ k: 'jobs', l: 'Jobs' }, { k: 'gross', l: 'Gross' }, { k: 'pct', l: '%' }] },
    { id: 'top', title: 'Top Bookings This Week', type: 'list', lines: 3 },
    { id: 'collective', title: 'Driven Collective', type: 'grid', rows: ['Network'], cols: [{ k: 'newdrivers', l: 'New Drivers Onboarded' }, { k: 'slots', l: 'Thu Onboarding Slots' }, { k: 'refusal', l: 'Corey First-Refusal' }, { k: 'paid', l: 'Total Paid to Network' }] },
    { id: 'collections', title: 'Outstanding Collections', type: 'list', lines: 3 },
    { id: 'notes', title: 'Week Notes', type: 'list', lines: 3 },
  ],
  monthly: [
    { id: 'summary', title: 'Monthly Summary', type: 'grid', rows: ['This Month'], cols: [{ k: 'bookings', l: 'Total Bookings' }, { k: 'gross', l: 'Gross Revenue' }, { k: 'cost', l: 'Driver / Cost' }, { k: 'net', l: 'Net to ACE' }, { k: 'goal', l: 'Monthly Goal' }, { k: 'lastnet', l: 'Last Month Net' }] },
    { id: 'weeks', title: 'Week by Week', type: 'grid', rows: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'], cols: [{ k: 'jobs', l: 'Jobs' }, { k: 'gross', l: 'Gross' }, { k: 'cost', l: 'Driver Cost' }, { k: 'net', l: 'Net' }, { k: 'out', l: 'Outstanding' }] },
    { id: 'lines', title: 'Revenue by Service Line', type: 'grid', rows: SERVICES, cols: [{ k: 'jobs', l: 'Jobs' }, { k: 'gross', l: 'Gross' }, { k: 'pct', l: '%' }] },
    { id: 'channels', title: 'Revenue by Channel', type: 'grid', rows: CHANNELS, cols: [{ k: 'rev', l: 'Revenue' }, { k: 'pct', l: '%' }] },
    { id: 'membership', title: 'Membership Revenue', type: 'grid', rows: ['Lobby', 'Condo', 'Penthouse'], cols: [{ k: 'members', l: 'Members' }, { k: 'rev', l: 'Revenue' }] },
    { id: 'consulting', title: 'Consulting Revenue', type: 'grid', rows: ['Discovery Sessions', 'Track A — Investor', 'Track B — Individual', 'Track C — Corporate', 'Track D / ACCD Cert'], cols: [{ k: 'sessions', l: 'Sessions' }, { k: 'rev', l: 'Revenue' }] },
    { id: 'accd', title: 'ACCD & Driven Collective', type: 'grid', rows: ['This Month'], cols: [{ k: 'enroll', l: 'ACCD Enrollments' }, { k: 'students', l: 'Active Students' }, { k: 'accdrev', l: 'ACCD Revenue' }, { k: 'drivers', l: 'Active Drivers' }, { k: 'hours', l: 'Driver Hours' }, { k: 'paid', l: 'Paid to Network' }] },
    { id: 'clients', title: 'Top Clients This Month', type: 'list', lines: 3 },
    { id: 'collections', title: 'Outstanding Collections', type: 'list', lines: 3 },
    { id: 'notes', title: 'Month Notes / Highlights', type: 'list', lines: 3 },
  ],
};

const periodDefault = (t) => {
  const d = new Date();
  if (t === 'daily') return d.toISOString().slice(0, 10);
  if (t === 'monthly') return d.toISOString().slice(0, 7);
  const onejan = new Date(d.getFullYear(), 0, 1);
  const wk = Math.ceil((((d - onejan) / 86400000) + onejan.getDay() + 1) / 7);
  return `${d.getFullYear()}-W${String(wk).padStart(2, '0')}`;
};

const OpsLogsTab = () => {
  const [logType, setLogType] = useState('daily');
  const [period, setPeriod] = useState(periodDefault('daily'));
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (t, p) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/command-center/ops-logs/${t}/${encodeURIComponent(p)}`);
      const d = await r.json();
      setData(d.data || {});
    } catch (_) { setData({}); }
    setLoading(false);
  }, []);
  useEffect(() => { load(logType, period); }, [logType, period, load]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/ops-logs/${logType}/${encodeURIComponent(period)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ data }),
      });
      if (!r.ok) throw new Error('Save failed');
      toast.success(`${logType} log saved for ${period}`);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const autofillDaily = async () => {
    setAutoBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/ops-logs/autofill/daily/${period}`);
      if (!r.ok) throw new Error('Auto-fill failed');
      const d = await r.json();
      if (!d.bookings_found) { toast.info(`No bookings found for ${period}`); setAutoBusy(false); return; }
      setData(p => {
        const next = { ...p, vehicles: { ...(p.vehicles || {}) }, extra: { ...(p.extra || {}) }, confirmed: { ...(p.confirmed || {}) } };
        Object.entries(d.vehicles || {}).forEach(([row, vals]) => {
          next.vehicles[row] = { ...(next.vehicles[row] || {}), ...vals };
        });
        next.extra.Today = { ...(next.extra.Today || {}), collected: d.collected.toFixed(2) };
        (d.confirmed_lines || []).forEach((line, i) => { next.confirmed[`line${i}`] = { v: line }; });
        return next;
      });
      const un = (d.unmatched || []).length;
      toast.success(`Filled from ${d.bookings_found} booking${d.bookings_found > 1 ? 's' : ''}${un ? ` · ${un} vehicle${un > 1 ? 's' : ''} unmatched` : ''}`);
      if (un) toast.warning(`Unmatched: ${d.unmatched.map(u => u.vehicle || '(no vehicle)').join(', ')} — add gross manually`);
    } catch (e) { toast.error('Auto-fill failed'); }
    setAutoBusy(false);
  };

  const rollupWeekly = async () => {
    setAutoBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/ops-logs/rollup/weekly/${encodeURIComponent(period)}`);
      if (!r.ok) throw new Error('Rollup failed');
      const d = await r.json();
      if (!d.daily_logs_found) { toast.info(`No saved daily logs found for ${period}. Save daily logs first.`); setAutoBusy(false); return; }
      setData(p => {
        const next = { ...p, days: { ...(p.days || {}) }, summary: { ...(p.summary || {}) } };
        Object.entries(d.days || {}).forEach(([day, vals]) => {
          next.days[day] = { ...(next.days[day] || {}), ...vals };
        });
        next.summary['This Week'] = { ...(next.summary['This Week'] || {}), ...d.summary };
        return next;
      });
      toast.success(`Rolled up ${d.daily_logs_found} daily log${d.daily_logs_found > 1 ? 's' : ''} into ${period}`);
    } catch (e) { toast.error('Rollup failed'); }
    setAutoBusy(false);
  };

  const get = (s, r, c) => data?.[s]?.[r]?.[c] ?? '';
  const set = (s, r, c, v) => setData(p => ({ ...p, [s]: { ...(p[s] || {}), [r]: { ...((p[s] || {})[r] || {}), [c]: v } } }));

  // Daily auto totals
  const dGross = DAILY_VEHICLES.reduce((s, v) => s + num(get('vehicles', v, 'gross')), 0);
  const dCost = ['JD Lawrence', 'Corey', 'Affiliate Driver 1', 'Affiliate Driver 2'].reduce((s, v) => s + num(get('drivers', v, 'owed')), 0);
  const dExp = EXPENSES.reduce((s, v) => s + num(get('expenses', v, 'amt')), 0);
  const dNet = dGross - dCost - dExp;
  const miles = num(get('extra', 'Today', 'miles'));

  const schema = SCHEMAS[logType];
  const switchType = (t) => { setLogType(t); setPeriod(periodDefault(t)); };

  return (
    <div className="qq-ops rounded-sm" data-testid="ops-logs-tab">
      <div className="qq-masthead flex items-center justify-between px-5 py-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl" style={{ color: '#C8102E' }}>♠</span>
          <div>
            <h2 className="qq-bebas text-3xl leading-none tracking-wide">ACE OPS LOGS</h2>
            <p className="qq-cond text-[10px] uppercase tracking-[0.3em] text-gray-500">Daily Ops · Weekly Rollup · Monthly P&amp;L</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {['daily', 'weekly', 'monthly'].map(t => (
            <button key={t} onClick={() => switchType(t)}
              className={`qq-cond text-[11px] uppercase tracking-[0.2em] px-4 py-2 border ${logType === t ? 'border-red-700 text-white bg-red-900/30' : 'border-[#2c2c2c] text-gray-500'}`}
              data-testid={`opslog-type-${t}`}>{t}</button>
          ))}
          <input type={logType === 'daily' ? 'date' : logType === 'weekly' ? 'week' : 'month'} value={period}
            onChange={e => setPeriod(e.target.value)} className="text-xs px-2 py-2 rounded-sm" data-testid="opslog-period" />
        </div>
      </div>

      <div className="p-5 space-y-6">
        {loading ? <p className="qq-cond text-xs text-gray-500 uppercase tracking-widest text-center py-8">Loading log…</p> : schema.map(sec => (
          <div key={sec.id} data-testid={`opslog-section-${sec.id}`}>
            <p className="qq-cond text-xs uppercase tracking-[0.25em] text-gray-500 border-b border-[#2c2c2c] pb-2 mb-3"><span style={{ color: '#C8102E' }}>♠</span> {sec.title}</p>
            {sec.type === 'grid' ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr>
                    <th className="qq-cond text-left text-[9px] uppercase tracking-widest text-gray-600 py-1 pr-2"> </th>
                    {sec.cols.map(c => <th key={c.k} className="qq-cond text-left text-[9px] uppercase tracking-widest text-gray-600 py-1 pr-2">{c.l}</th>)}
                  </tr></thead>
                  <tbody>
                    {sec.rows.map(rw => (
                      <tr key={rw}>
                        <td className="qq-cond text-[11px] text-gray-400 uppercase tracking-wider pr-3 py-1 whitespace-nowrap">{rw}</td>
                        {sec.cols.map(c => (
                          <td key={c.k} className={`pr-2 py-1 ${c.wide ? 'w-full' : ''}`}>
                            <input value={get(sec.id, rw, c.k)} onChange={e => set(sec.id, rw, c.k, e.target.value)}
                              className={`px-2 py-1.5 text-xs rounded-sm w-full ${c.wide ? '' : 'min-w-[80px]'}`}
                              data-testid={`opslog-${sec.id}-${rw.replace(/[^a-zA-Z0-9]+/g, '_')}-${c.k}`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="space-y-1.5">
                {Array.from({ length: sec.lines }).map((_, i) => (
                  <input key={i} value={get(sec.id, `line${i}`, 'v')} onChange={e => set(sec.id, `line${i}`, 'v', e.target.value)}
                    placeholder={`${i + 1}.`} className="w-full px-2 py-1.5 text-xs rounded-sm" data-testid={`opslog-${sec.id}-line${i}`} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Sticky totals + actions */}
      <div className="qq-toolbar sticky bottom-0 z-20 flex items-center justify-between gap-3 px-5 py-3 flex-wrap">
        <div className="flex items-center gap-5 flex-wrap" data-testid="opslog-totals">
          {logType === 'daily' ? (<>
            <div><p className="qq-cond text-[9px] uppercase tracking-[0.25em] text-gray-500">Gross</p><p className="qq-bebas text-xl leading-none text-white">{$(dGross)}</p></div>
            <div><p className="qq-cond text-[9px] uppercase tracking-[0.25em] text-gray-500">Driver + Exp</p><p className="qq-bebas text-xl leading-none text-gray-300">{$(dCost + dExp)}</p></div>
            <div><p className="qq-cond text-[9px] uppercase tracking-[0.25em] text-gray-500">Net to ACE</p><p className="qq-bebas text-xl leading-none" style={{ color: '#C8102E' }}>{$(dNet)}</p></div>
            <div><p className="qq-cond text-[9px] uppercase tracking-[0.25em] text-gray-500">Tax Reserve 30%</p><p className="qq-bebas text-xl leading-none text-amber-500">{$(Math.max(dNet * 0.3, 0))}</p></div>
            <div><p className="qq-cond text-[9px] uppercase tracking-[0.25em] text-gray-500">Mileage Ded. (70¢)</p><p className="qq-bebas text-xl leading-none text-gray-300">{$(miles * 0.7)}</p></div>
          </>) : (
            <p className="qq-cond text-[10px] uppercase tracking-[0.25em] text-gray-500">{logType} log · {period}</p>
          )}
        </div>
        <div className="flex gap-2">
          {logType === 'daily' && (
            <Button onClick={autofillDaily} disabled={autoBusy || loading} size="sm" variant="outline"
              className="h-9 text-xs qq-cond uppercase tracking-widest border-amber-700/60 bg-transparent text-amber-400 hover:text-amber-300 hover:border-amber-500"
              data-testid="opslog-autofill-btn">
              {autoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Zap className="h-3.5 w-3.5 mr-1" />Auto-Fill From Bookings</>}
            </Button>
          )}
          {logType === 'weekly' && (
            <Button onClick={rollupWeekly} disabled={autoBusy || loading} size="sm" variant="outline"
              className="h-9 text-xs qq-cond uppercase tracking-widest border-amber-700/60 bg-transparent text-amber-400 hover:text-amber-300 hover:border-amber-500"
              data-testid="opslog-rollup-btn">
              {autoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1" />Roll Up From Daily Logs</>}
            </Button>
          )}
          <Button onClick={() => window.print()} size="sm" variant="outline" className="h-9 text-xs qq-cond uppercase tracking-widest border-[#3c3c3c] bg-transparent text-gray-300 hover:text-white"><Printer className="h-3.5 w-3.5 mr-1" />Print</Button>
          <Button onClick={save} disabled={busy} size="sm" className="h-9 text-xs qq-cond uppercase tracking-widest bg-red-700 hover:bg-red-600" data-testid="opslog-save">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" />Save Log</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OpsLogsTab;
